import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "@phosphor-icons/react";
import { IMG } from "../../data";
import { MaskLine, Overline } from "./primitives";

export const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMG.hero} alt="Emporium London hospitality tabletop with ceramics, cutlery, teapot and serving tray" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 z-[1] bg-black/25" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[var(--bg-primary)] via-black/50 to-black/40" />

      <div className="relative z-10 min-h-screen flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-16 md:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }} className="mb-8">
          <Overline>Objects &amp; Gifting for Hospitality</Overline>
        </motion.div>

        <h1 className="font-serif font-light leading-[0.9] tracking-tight text-[#F7F2E8] text-5xl sm:text-7xl lg:text-[7vw]">
          <MaskLine delay={0.55}>Your brand.</MaskLine>
          <MaskLine delay={0.68} className="italic text-[#DBB876]">Your product.</MaskLine>
          <MaskLine delay={0.81}>In every detail.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end"
        >
          <p className="md:col-span-5 md:col-start-8 text-[#EAE2D3] text-base sm:text-lg leading-relaxed max-w-md">
            We help restaurants, hotels and businesses turn physical products into powerful marketing, customer engagement and new revenue channels.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-collections-btn"
            onClick={() => navigate("/collections")}
            className="inline-flex items-center gap-2 bg-[#F2EBDD] text-[#2B2620] px-8 h-14 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-[#DBB876] transition-colors duration-500"
          >
            Explore Collections <ArrowRight size={16} />
          </button>
          <button
            data-testid="hero-enquiry-btn"
            onClick={() => navigate("/contact")}
            className="inline-flex items-center gap-2 border border-white/40 text-[#F7F2E8] px-8 h-14 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-white/10 transition-colors duration-500"
          >
            Request a Selection
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 1 }}
          className="mt-14 flex items-center gap-3 text-[#D2C8B6]"
        >
          <ArrowDown size={16} className="animate-bounce" />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll to explore</span>
        </motion.div>
      </div>
    </section>
  );
};
